import {
  clampFreq,
  midiToFreq,
  noiseBuffer,
  type NoiseColor,
  pluckBuffer,
  VOWELS,
  type Vowel,
} from './synth';
import type { InstrumentId, Layer } from './themes';

/**
 * Synthesised orchestra for the generative score. Every note builds a handful
 * of short-lived nodes that stop themselves; per-section processing (pad
 * filter, string chorus, choir formants) lives in shared chains so the cost
 * per note stays small. Nothing long-lived ever connects *into* a per-note
 * node (that would keep finished notes alive), so vibrato comes from a
 * modulated-delay chorus on the chain rather than a shared LFO.
 */

/** Per-instrument output level; the whole arrangement then goes through the theme level. */
export const INSTRUMENT_LEVEL: Record<InstrumentId, number> = {
  pad: 0.0885,
  strings: 0.0675,
  choir: 0.1155,
  choirOo: 0.0585,
  harp: 0.495,
  oud: 0.645,
  keys: 0.168,
  bell: 0.09,
  celesta: 0.1605,
  tubular: 0.09,
  flute: 0.1455,
  brass: 0.0915,
  horn: 0.1192,
  bass: 0.1095,
  ostinato: 0.18,
  drone: 0.063,
  taiko: 0.285,
  timpani: 0.1755,
  snare: 0.54,
  shaker: 0.45,
  frame: 0.324,
  tek: 0.825,
  swell: 0.165,
  gong: 0.123,
};

export const PERCUSSION: ReadonlySet<InstrumentId> = new Set<InstrumentId>([
  'taiko',
  'timpani',
  'snare',
  'shaker',
  'frame',
  'tek',
  'swell',
  'gong',
  'tubular',
]);

type ChainName =
  'pad' | 'strings' | 'choir' | 'choirOo' | 'bass' | 'ostinato' | 'drone' | 'pluck' | 'plain';

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

/**
 * Attack / decay-to-sustain / release. Returns the time the voice may stop.
 * The note-off happens at `t + max(dur, attack)`.
 */
function adsr(
  p: AudioParam,
  t: number,
  dur: number,
  a: number,
  d: number,
  s: number,
  r: number,
  peak: number,
): number {
  const off = t + Math.max(dur, a);
  p.setValueAtTime(0, t);
  p.linearRampToValueAtTime(peak, t + a);
  p.setTargetAtTime(peak * s, t + a, Math.max(0.002, d / 3));
  p.setTargetAtTime(0, off, Math.max(0.002, r / 5));
  return off + r;
}

/** Percussive envelope: fast attack then exponential decay with time constant `tau`. */
function perc(p: AudioParam, t: number, peak: number, tau: number, attack = 0.002): number {
  p.setValueAtTime(0, t);
  p.linearRampToValueAtTime(peak, t + attack);
  p.setTargetAtTime(0, t + attack, tau);
  return t + attack + tau * 6.5;
}

export class InstrumentBank {
  private readonly chains = new Map<string, AudioNode>();
  private readonly persistent: AudioScheduledSourceNode[] = [];
  private readonly owned: AudioNode[] = [];
  private disposed = false;

  constructor(
    readonly ctx: BaseAudioContext,
    private readonly layers: Record<Layer, AudioNode>,
    private readonly opts: { padCutoff?: number } = {},
  ) {}

  play(inst: InstrumentId, layer: Layer, t: number, dur: number, midi: number, vel: number): void {
    if (this.disposed) return;
    const f = midiToFreq(midi);
    const peak = INSTRUMENT_LEVEL[inst] * vel;
    switch (inst) {
      case 'pad':
        return this.pad(layer, t, dur, f, peak);
      case 'strings':
        return this.strings(layer, t, dur, f, peak);
      case 'choir':
      case 'choirOo':
        return this.choir(inst, layer, t, dur, f, peak);
      case 'harp':
      case 'oud':
        return this.pluck(inst, layer, t, midi, peak);
      case 'keys':
        return this.keys(layer, t, dur, f, peak);
      case 'bell':
        return this.fmBell(layer, t, f, peak, 3.5, 1.2, 0.8, 3.2);
      case 'celesta':
        return this.fmBell(layer, t, f, peak, 4, 0.55, 0.4, 1.8);
      case 'tubular':
        return this.tubular(layer, t, f, peak);
      case 'flute':
        return this.flute(layer, t, dur, f, peak);
      case 'brass':
        return this.brass(layer, t, dur, f, peak, false);
      case 'horn':
        return this.brass(layer, t, dur, f, peak, true);
      case 'bass':
        return this.bass(layer, t, dur, f, peak);
      case 'ostinato':
        return this.ostinato(layer, t, dur, f, peak);
      case 'drone':
        return this.drone(layer, t, dur, f, peak);
      case 'taiko':
        return this.taiko(layer, t, peak);
      case 'timpani':
        return this.timpani(layer, t, f, peak);
      case 'snare':
        return this.snare(layer, t, peak);
      case 'shaker':
        return this.shaker(layer, t, peak);
      case 'frame':
        return this.frame(layer, t, peak);
      case 'tek':
        return this.tek(layer, t, peak);
      case 'swell':
        return this.swell(layer, t, dur, peak);
      case 'gong':
        return this.gong(layer, t, peak);
    }
  }

  /** Stops shared LFOs and disconnects shared chains (notes already scheduled keep ringing into nothing). */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    const now = this.ctx.currentTime;
    for (const s of this.persistent) {
      try {
        s.stop(now);
      } catch {
        // already stopped
      }
    }
    for (const n of this.owned) n.disconnect();
    this.chains.clear();
  }

  // ------------------------------------------------------------------ plumbing

  private gain(dest: AudioNode | AudioParam | null, value = 0): GainNode {
    const g = this.ctx.createGain();
    g.gain.value = value;
    if (dest) {
      if ('connect' in dest) g.connect(dest);
      else g.connect(dest);
    }
    return g;
  }

  private osc(
    type: OscillatorType,
    freq: number,
    t: number,
    stop: number,
    dest: AudioNode,
    detune = 0,
  ): OscillatorNode {
    const o = this.ctx.createOscillator();
    o.type = type;
    o.frequency.value = clampFreq(freq);
    if (detune) o.detune.value = detune;
    o.connect(dest);
    o.start(t);
    o.stop(stop);
    return o;
  }

  private noise(
    color: NoiseColor,
    t: number,
    stop: number,
    dest: AudioNode,
  ): AudioBufferSourceNode {
    const b = noiseBuffer(this.ctx, color);
    const src = this.ctx.createBufferSource();
    src.buffer = b;
    src.loop = true;
    src.connect(dest);
    src.start(t, Math.random() * Math.max(0, b.duration - 0.2));
    src.stop(stop);
    return src;
  }

  private filter(
    type: BiquadFilterType,
    freq: number,
    q: number,
    dest: AudioNode | null,
  ): BiquadFilterNode {
    const f = this.ctx.createBiquadFilter();
    f.type = type;
    f.frequency.value = clampFreq(freq);
    f.Q.value = q;
    if (dest) f.connect(dest);
    return f;
  }

  private own<T extends AudioNode>(n: T): T {
    this.owned.push(n);
    return n;
  }

  private lfo(rate: number, depth: number, param: AudioParam, type: OscillatorType = 'sine'): void {
    const o = this.ctx.createOscillator();
    o.type = type;
    o.frequency.value = rate;
    const g = this.own(this.gain(null, depth));
    o.connect(g);
    g.connect(param);
    o.start();
    this.persistent.push(o);
    this.own(o);
  }

  /** Stereo modulated-delay chorus: gives sections width and a gentle ensemble vibrato. */
  private chorus(input: AudioNode, dest: AudioNode, depth = 0.0018, mix = 0.5): void {
    input.connect(dest);
    const pairs: [number, number][] = [
      [-0.75, 0.43],
      [0.75, 0.57],
    ];
    for (const [pan, rate] of pairs) {
      const d = this.own(this.ctx.createDelay(0.05));
      d.delayTime.value = 0.011;
      this.lfo(rate, depth, d.delayTime);
      const wg = this.own(this.gain(null, mix));
      input.connect(d);
      d.connect(wg);
      if (typeof this.ctx.createStereoPanner === 'function') {
        const p = this.own(this.ctx.createStereoPanner());
        p.pan.value = pan;
        wg.connect(p);
        p.connect(dest);
      } else {
        wg.connect(dest);
      }
    }
  }

  private chain(name: ChainName, layer: Layer): AudioNode {
    const key = `${name}:${layer}`;
    const hit = this.chains.get(key);
    if (hit) return hit;
    const dest = this.layers[layer];
    let input: AudioNode;
    switch (name) {
      case 'pad': {
        const cutoff = this.opts.padCutoff ?? 1100;
        const lp = this.own(this.filter('lowpass', cutoff, 0.6, null));
        this.lfo(0.061, cutoff * 0.28, lp.frequency);
        this.chorus(lp, dest, 0.0012, 0.35);
        input = lp;
        break;
      }
      case 'strings': {
        const hp = this.own(this.filter('highpass', 85, 0.7, null));
        const lp = this.own(this.filter('lowpass', 3400, 0.6, null));
        hp.connect(lp);
        this.chorus(lp, dest, 0.0016, 0.5);
        input = hp;
        break;
      }
      case 'choir':
      case 'choirOo': {
        const vowel: Vowel = name === 'choir' ? 'ah' : 'oo';
        const sum = this.own(this.gain(null, 1));
        input = this.own(this.gain(null, 1));
        for (const [f, q, g] of VOWELS[vowel]) {
          const bp = this.own(this.filter('bandpass', f, q, this.own(this.gain(sum, g * 1.7))));
          input.connect(bp);
          // Slow vowel drift keeps sustained chords alive.
          this.lfo(0.09 + f / 20000, f * 0.05, bp.frequency);
        }
        const body = this.own(this.filter('lowpass', 800, 0.7, this.own(this.gain(sum, 0.2))));
        input.connect(body);
        this.chorus(sum, dest, 0.002, 0.55);
        break;
      }
      case 'bass':
        input = this.own(this.filter('lowpass', 650, 0.9, dest));
        break;
      case 'ostinato': {
        const lp = this.own(this.filter('lowpass', 2600, 0.7, dest));
        input = this.own(this.filter('highpass', 140, 0.7, lp));
        break;
      }
      case 'drone': {
        const lp = this.own(this.filter('lowpass', 420, 1.1, dest));
        this.lfo(0.047, 140, lp.frequency);
        input = lp;
        break;
      }
      case 'pluck':
        input = this.own(this.filter('lowpass', 5200, 0.5, dest));
        break;
      default:
        input = this.own(this.gain(dest, 1));
    }
    this.chains.set(key, input);
    return input;
  }

  // ------------------------------------------------------------------ sustained sections

  private pad(layer: Layer, t: number, dur: number, f: number, peak: number): void {
    const g = this.gain(this.chain('pad', layer));
    const stop = adsr(
      g.gain,
      t,
      dur,
      clamp(dur * 0.3, 0.3, 1.6),
      0.8,
      0.8,
      clamp(0.8 + dur * 0.15, 0.8, 2.2),
      peak,
    );
    this.osc('sawtooth', f, t, stop, g, -8 + Math.random() * 2);
    this.osc('sawtooth', f, t, stop, g, 8 - Math.random() * 2);
  }

  private strings(layer: Layer, t: number, dur: number, f: number, peak: number): void {
    const g = this.gain(this.chain('strings', layer));
    const stop = adsr(g.gain, t, dur, clamp(dur * 0.22, 0.12, 0.7), 0.5, 0.85, 0.75, peak);
    this.osc('sawtooth', f, t, stop, g, -5);
    this.osc('sawtooth', f, t, stop, g, 5);
  }

  private choir(
    inst: 'choir' | 'choirOo',
    layer: Layer,
    t: number,
    dur: number,
    f: number,
    peak: number,
  ): void {
    const g = this.gain(this.chain(inst, layer));
    const stop = adsr(g.gain, t, dur, clamp(dur * 0.3, 0.35, 0.9), 0.6, 0.85, 1.1, peak);
    this.osc('sawtooth', f, t, stop, g, -9);
    this.osc('sawtooth', f, t, stop, g, 9);
    this.osc('triangle', f, t, stop, g, 0);
  }

  private drone(layer: Layer, t: number, dur: number, f: number, peak: number): void {
    const g = this.gain(this.chain('drone', layer));
    const stop = adsr(g.gain, t, dur, Math.min(3, dur * 0.3), 1, 0.9, 3, peak);
    this.osc('sawtooth', f, t, stop, g, -4);
    this.osc('sawtooth', f, t, stop, g, 4);
    this.osc('sine', f / 2, t, stop, g);
  }

  private brass(
    layer: Layer,
    t: number,
    dur: number,
    f: number,
    peak: number,
    mellow: boolean,
  ): void {
    const g = this.gain(this.chain('plain', layer));
    const stop = adsr(g.gain, t, dur, mellow ? 0.06 : 0.035, 0.3, 0.8, 0.22, peak);
    const lp = this.filter('lowpass', f * 1.1, 1.3, g);
    const vel = clamp(peak / INSTRUMENT_LEVEL[mellow ? 'horn' : 'brass'], 0, 1);
    const top = clampFreq(f * (mellow ? 3.2 : 5.5) * (0.65 + 0.5 * vel));
    lp.frequency.setValueAtTime(clampFreq(f * 1.1), t);
    lp.frequency.exponentialRampToValueAtTime(top, t + 0.07);
    lp.frequency.setTargetAtTime(clampFreq(f * (mellow ? 2.1 : 3.2)), t + 0.07, 0.15);
    lp.frequency.setTargetAtTime(clampFreq(f * 1.1), t + Math.max(dur, 0.06), 0.1);
    for (const det of [-6, 6]) {
      const o = this.osc('sawtooth', f, t, stop, lp, det);
      o.detune.setValueAtTime(det - 35, t);
      o.detune.linearRampToValueAtTime(det, t + 0.06);
    }
  }

  private flute(layer: Layer, t: number, dur: number, f: number, peak: number): void {
    const g = this.gain(this.chain('plain', layer));
    const stop = adsr(g.gain, t, dur, 0.07, 0.25, 0.85, 0.16, peak);
    const car = this.osc('sine', f, t, stop, g);
    const ovt = this.gain(g, 0.12);
    this.osc('triangle', f * 2, t, stop, ovt);
    // Delayed vibrato, per note.
    if (dur > 0.35) {
      const vg = this.gain(null, 0);
      vg.connect(car.frequency);
      vg.gain.setValueAtTime(0, t);
      vg.gain.linearRampToValueAtTime(f * 0.006, t + Math.min(0.6, dur));
      const lfo = this.ctx.createOscillator();
      lfo.frequency.value = 5.1;
      lfo.connect(vg);
      lfo.start(t);
      lfo.stop(stop);
    }
    // Breath.
    const bg = this.gain(this.chain('plain', layer));
    adsr(bg.gain, t, dur, 0.04, 0.2, 0.35, 0.1, peak * 0.22);
    this.noise('white', t, stop, this.filter('bandpass', f * 2, 1.6, bg));
  }

  private bass(layer: Layer, t: number, dur: number, f: number, peak: number): void {
    const g = this.gain(this.chain('bass', layer));
    const stop = adsr(g.gain, t, dur, 0.008, 0.3, 0.7, 0.12, peak);
    this.osc('sawtooth', f, t, stop, this.gain(g, 0.55));
    this.osc('sine', f, t, stop, g);
  }

  private ostinato(layer: Layer, t: number, dur: number, f: number, peak: number): void {
    const g = this.gain(this.chain('ostinato', layer));
    const stop = adsr(g.gain, t, dur, 0.006, 0.12, 0.45, 0.08, peak);
    this.osc('sawtooth', f, t, stop, g, -7);
    this.osc('sawtooth', f, t, stop, g, 7);
  }

  // ------------------------------------------------------------------ plucked / struck

  private pluck(inst: 'harp' | 'oud', layer: Layer, t: number, midi: number, peak: number): void {
    const { buffer, rate } = pluckBuffer(this.ctx, midi, inst);
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.playbackRate.value = rate;
    const g = this.gain(this.chain('pluck', layer), peak);
    src.connect(g);
    src.start(t);
    src.stop(t + buffer.duration / rate + 0.02);
  }

  /** Soft FM electric-piano / music-box keys. */
  private keys(layer: Layer, t: number, dur: number, f: number, peak: number): void {
    const g = this.gain(this.chain('plain', layer));
    const tau = 0.55 * (440 / f) ** 0.3;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(peak, t + 0.004);
    g.gain.setTargetAtTime(0, t + 0.004, tau);
    g.gain.setTargetAtTime(0, t + Math.max(0.01, dur), 0.07);
    const stop = t + Math.min(dur + 0.5, tau * 6.5);
    const car = this.osc('sine', f, t, stop, g);
    const mg = this.gain(car.frequency);
    mg.gain.setValueAtTime(f * 1.5, t);
    mg.gain.setTargetAtTime(f * 0.25, t, 0.28);
    this.osc('sine', f, t, stop, mg);
    const tg = this.gain(car.frequency);
    tg.gain.setValueAtTime(f * 0.8, t);
    tg.gain.setTargetAtTime(0, t, 0.02);
    this.osc('sine', f * 14, t, Math.min(stop, t + 0.2), tg);
  }

  private fmBell(
    layer: Layer,
    t: number,
    f: number,
    peak: number,
    ratio: number,
    index: number,
    tau: number,
    len: number,
  ): void {
    const g = this.gain(this.chain('plain', layer));
    perc(g.gain, t, peak, tau, 0.002);
    const stop = t + len;
    const car = this.osc('sine', f, t, stop, g);
    const mg = this.gain(car.frequency);
    mg.gain.setValueAtTime(f * ratio * index, t);
    mg.gain.setTargetAtTime(0, t, tau * 0.6);
    this.osc('sine', f * ratio, t, stop, mg);
  }

  private tubular(layer: Layer, t: number, f: number, peak: number): void {
    const out = this.chain('plain', layer);
    const parts: [number, number, number][] = [
      [1, 1, 1.6],
      [2.76, 0.5, 1.0],
      [5.4, 0.25, 0.6],
      [8.93, 0.12, 0.35],
    ];
    for (const [ratio, amp, tau] of parts) {
      const g = this.gain(out);
      const stop = perc(g.gain, t, peak * amp, tau, 0.002);
      this.osc('sine', f * ratio, t, stop, g);
    }
  }

  // ------------------------------------------------------------------ percussion

  private taiko(layer: Layer, t: number, peak: number): void {
    const out = this.chain('plain', layer);
    const k = 0.96 + Math.random() * 0.08;
    const f0 = 118 * k;
    const g = this.gain(out);
    const stop = perc(g.gain, t, peak, 0.14);
    const o = this.osc('sine', f0, t, stop, g);
    o.frequency.setValueAtTime(f0, t);
    o.frequency.exponentialRampToValueAtTime(55, t + 0.18);
    // Mid "boom" so the drum still reads on small speakers.
    const body = this.gain(out);
    const b = this.osc('triangle', 190 * k, t, perc(body.gain, t, peak * 0.35, 0.06), body);
    b.frequency.setValueAtTime(190 * k, t);
    b.frequency.exponentialRampToValueAtTime(120 * k, t + 0.08);
    const sub = this.gain(out);
    this.osc('sine', 52, t, perc(sub.gain, t, peak * 0.2, 0.18, 0.004), sub);
    const skin = this.gain(out);
    this.noise(
      'pink',
      t,
      perc(skin.gain, t, peak * 0.45, 0.03),
      this.filter('lowpass', 1200, 0.7, skin),
    );
  }

  private timpani(layer: Layer, t: number, f: number, peak: number): void {
    const out = this.chain('plain', layer);
    const partials: [number, number, number][] = [
      [1, 1, 0.55],
      [1.5, 0.35, 0.35],
      [1.98, 0.2, 0.25],
    ];
    for (const [ratio, amp, tau] of partials) {
      const g = this.gain(out);
      const stop = perc(g.gain, t, peak * amp, tau, 0.003);
      const o = this.osc('sine', f * ratio, t, stop, g);
      o.frequency.setValueAtTime(f * ratio * 1.025, t);
      o.frequency.exponentialRampToValueAtTime(f * ratio, t + 0.06);
    }
    const mallet = this.gain(out);
    this.noise(
      'pink',
      t,
      perc(mallet.gain, t, peak * 0.3, 0.02),
      this.filter('lowpass', 700, 0.7, mallet),
    );
  }

  private snare(layer: Layer, t: number, peak: number): void {
    const out = this.chain('plain', layer);
    const body = this.gain(out);
    this.noise(
      'white',
      t,
      perc(body.gain, t, peak, 0.045),
      this.filter('bandpass', 1800, 0.9, body),
    );
    const wires = this.gain(out);
    this.noise(
      'white',
      t,
      perc(wires.gain, t, peak * 0.45, 0.03),
      this.filter('highpass', 5000, 0.7, wires),
    );
    const tone = this.gain(out);
    const o = this.osc('triangle', 190, t, perc(tone.gain, t, peak * 0.5, 0.03), tone);
    o.frequency.setValueAtTime(190, t);
    o.frequency.exponentialRampToValueAtTime(165, t + 0.05);
  }

  private shaker(layer: Layer, t: number, peak: number): void {
    const g = this.gain(this.chain('plain', layer));
    const hp = this.filter('highpass', 4000, 0.7, g);
    this.noise(
      'white',
      t,
      perc(g.gain, t, peak, 0.028, 0.004),
      this.filter('bandpass', 6500, 0.7, hp),
    );
  }

  private frame(layer: Layer, t: number, peak: number): void {
    const out = this.chain('plain', layer);
    const g = this.gain(out);
    const o = this.osc('sine', 92, t, perc(g.gain, t, peak, 0.12), g);
    o.frequency.setValueAtTime(92, t);
    o.frequency.exponentialRampToValueAtTime(62, t + 0.08);
    const skin = this.gain(out);
    this.noise(
      'pink',
      t,
      perc(skin.gain, t, peak * 0.3, 0.02),
      this.filter('lowpass', 600, 0.7, skin),
    );
  }

  private tek(layer: Layer, t: number, peak: number): void {
    const out = this.chain('plain', layer);
    const g = this.gain(out);
    this.noise('white', t, perc(g.gain, t, peak, 0.025), this.filter('bandpass', 2600, 1.5, g));
    const tone = this.gain(out);
    this.osc('sine', 650, t, perc(tone.gain, t, peak * 0.35, 0.015), tone);
  }

  /** Reverse-cymbal swell that lands on the next downbeat. */
  private swell(layer: Layer, t: number, dur: number, peak: number): void {
    const g = this.gain(this.chain('plain', layer));
    const end = t + Math.max(0.2, dur);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), end);
    g.gain.linearRampToValueAtTime(0, end + 0.05);
    const bp = this.filter('bandpass', 2500, 0.6, g);
    bp.frequency.setValueAtTime(2500, t);
    bp.frequency.exponentialRampToValueAtTime(9000, end);
    this.noise('white', t, end + 0.08, this.filter('highpass', 2000, 0.7, bp));
  }

  private gong(layer: Layer, t: number, peak: number): void {
    const out = this.chain('plain', layer);
    const f = 68 + Math.random() * 6;
    const parts: [number, number, number, number][] = [
      [1, 1, 1.3, 0.003],
      [1.47, 0.7, 1.1, 0.12],
      [2.09, 0.5, 0.9, 0.2],
      [2.56, 0.4, 0.7, 0.25],
      [3.52, 0.25, 0.5, 0.3],
    ];
    for (const [ratio, amp, tau, attack] of parts) {
      const g = this.gain(out);
      const stop = perc(g.gain, t, peak * amp, tau, attack);
      this.osc('sine', f * ratio, t, stop, g);
    }
    const strike = this.gain(out);
    this.noise(
      'pink',
      t,
      perc(strike.gain, t, peak * 0.5, 0.05),
      this.filter('lowpass', 1200, 0.7, strike),
    );
  }
}
